import { PhoneRevealCreditsGate } from "@/components/contact-reveal/phone-reveal-credits-gate";
import { CopyableContactButton } from "@/components/data-table-company-employees/copyable-contact-button";
import { SignalEmailDialog } from "@/components/data-table-company-employees/dialog-signal-email";
import { SignalPhoneDialog } from "@/components/data-table-company-employees/dialog-signal-phone";
import EmployeeIntegrationsSync from "@/components/integrations/employee/employee-integrations-sync";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Spinner } from "@/components/ui/spinner";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useTeams } from "@/contexts/teams-provider";
import { useApi } from "@/hooks/useApi";
import { useConnectedCrmIntegrations } from "@/hooks/useConnectedCrmIntegrations";
import { useIsContactRevealOffered } from "@/hooks/useIsContactRevealOffered";
import { useMarqueeCopy } from "@/hooks/useMarqueeCopy";
import { useRateLimitWarning } from "@/hooks/useRateLimitWarning";
import { useTranslation } from "@/hooks/useTranslation";
import { getApiErrorStatus, getApiRateLimit } from "@/lib/api-error";
import { executeWithMinimumTime } from "@/lib/execute-with-minimum-time";
import { cn } from "@/lib/utils";
import { EmployeeDTO } from "@shaarp/common";
import { ToolUIPart } from "ai";
import {
  BanIcon,
  BotIcon,
  CircleAlertIcon,
  MailIcon,
  PhoneCallIcon,
  PhoneIcon,
  SendIcon,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { RiLinkedinBoxFill, RiLinkedinFill } from "react-icons/ri";

type ContactChannel = "email" | "phone";

/** Live per-channel cost/availability, as returned by `contact-reveal/state`. */
type PreviewChannel = {
  channel: ContactChannel;
  cost: number;
  alreadyRevealed: boolean;
  blacklisted: boolean;
  /**
   * Part of the API contract (the assistant uses it to phrase its answer). The
   * card no longer gates the button on it — `PhoneRevealCreditsGate` decides
   * from the live balance, so it can offer a top-up instead of a dead end.
   */
  canAfford: boolean;
  value: string | null;
  /** Already searched for this team, nothing found, still within the retry window. */
  searchedNotFound?: boolean;
  /** A user reported the value as wrong: the API withholds it. */
  reported?: boolean;
};

/**
 * What survives in a persisted message: the channel name only. Cost, balance,
 * blacklist status and any already-owned value are stripped server-side because
 * they go stale (and must not outlive a contact's opt-out), so the card always
 * refetches them.
 */
export type PersistedChannel = { channel: ContactChannel } & Partial<PreviewChannel>;

type ContactRevealStateResponse = {
  teamBalance: number;
  channels: PreviewChannel[];
  /** The company of the base the contact's domain points to, if any. */
  company?: { id: string; name: string } | null;
};

/** Response from `contact-reveal/select`: the picked contact, ready to render. */
type ContactRevealSelection = ContactRevealStateResponse & {
  employeeId: string;
  contact: PreviewContact;
};

export type PreviewContact = {
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  company: string | null;
  /** Optional: absent on cards persisted before these fields were added. */
  city?: string | null;
  country?: string | null;
  linkedInUrl: string | null;
  photoUrl?: string | null;
};

type PreviewCandidate = {
  employeeId?: string;
  apolloId?: string;
  firstName: string | null;
  lastName: string | null;
  jobTitle: string | null;
  company: string | null;
  city: string | null;
  country: string | null;
  linkedInUrl?: string | null;
  photoUrl?: string | null;
};

type PreviewOutput =
  | {
      status: "ready";
      employeeId: string;
      contact: PreviewContact;
      teamBalance?: number;
      channels: PersistedChannel[];
    }
  | { status: "ambiguous"; message: string; candidates: PreviewCandidate[] }
  | { status: "forbidden"; message: string }
  /**
   * The identified contact resolved fine, but none of the requested channels
   * are included in the team's plan (e.g. phone reveal turned off for this
   * team) — distinct from `forbidden`, which means nothing works at all.
   */
  | { status: "channel_unavailable"; message: string }
  | { status: "not_found" | "error"; message: string };

/** Response from the direct (button-driven) reveal endpoint. */
type RevealApiResponse = {
  status: string;
  value: string | null;
  pending: boolean;
  blacklisted?: boolean;
};

// Wide enough for a long address and its three actions on one line.
const CARD_CLASS = "w-full max-w-xl p-4 space-y-3";
// The homonym picker is a table → let it use the full message width.
const AMBIGUOUS_CARD_CLASS = "w-full p-4 space-y-3";
const MAX_POLL_ATTEMPTS = 15;
const POLL_INTERVAL_MS = 4000;
// Minimum spinner time on a reveal so an already-in-DB value doesn't flash
// instantly (matches the main search reveal behaviour).
const MIN_REVEAL_TIME_MS = 2000;
// A picked candidate is priced for both channels: the assistant's original
// `channel` input is not part of the tool output the card renders from.
const SELECTABLE_CHANNELS: ContactChannel[] = ["email", "phone"];

const fullName = (c: {
  firstName: string | null;
  lastName: string | null;
}): string => [c.firstName, c.lastName].filter(Boolean).join(" ").trim();

const initialsOf = (c: {
  firstName: string | null;
  lastName: string | null;
}): string =>
  `${c.firstName?.charAt(0) ?? ""}${c.lastName?.charAt(0) ?? ""}`.toUpperCase() ||
  "?";

const locationOf = (c: {
  city?: string | null;
  country?: string | null;
}): string => [c.city, c.country].filter(Boolean).join(", ");

/** Sends a message in the conversation the card belongs to. */
type SendMessage = (text: string) => void;

/**
 * The contact as the employee components expect it, built from what the card
 * holds. The signal dialogs and the CRM export read the id, the identity and
 * the revealed values; the rest is filled with neutral defaults.
 */
const toEmployeeDTO = (
  employeeId: string,
  contact: PreviewContact,
  values: { email: string | null; phone: string | null },
  integrations: EmployeeDTO["integrations"],
  isEmailChannelOpen: boolean,
  companyName: string | null
): EmployeeDTO => ({
  id: employeeId,
  domain: contact.company ?? "",
  firstName: contact.firstName,
  lastName: contact.lastName,
  jobTitle: contact.jobTitle,
  city: contact.city ?? null,
  country: contact.country ?? null,
  linkedInUrl: contact.linkedInUrl,
  photoUrl: contact.photoUrl ?? null,
  email: values.email,
  // A team whose email channel is cut gets no email from the CRM export
  // either: the export reveals one only when the status leaves it possible.
  emailStatus: values.email
    ? "VERIFIED"
    : isEmailChannelOpen
      ? "UNKNOWN"
      : "UNAVAILABLE",
  phone: values.phone,
  phoneStatus: values.phone ? "VERIFIED" : "UNKNOWN",
  employedFrom: null,
  level: "DEFAULT",
  suggestedOrder: null,
  suggestedReason: null,
  companyName: companyName ?? contact.company ?? undefined,
  integrations,
});

/** A channel that holds no value to act on: why, and what can still be done. */
function ChannelStatusRow({
  icon,
  message,
  tone = "muted",
  action,
}: {
  icon: React.ReactNode;
  message: string;
  tone?: "muted" | "destructive";
  action?: React.ReactNode;
}) {
  return (
    <div className="flex gap-2 items-center px-3 min-h-9 text-sm rounded-md bg-muted/60">
      {icon}
      <span
        className={cn(
          "flex-1 py-2 min-w-0 truncate",
          tone === "destructive" ? "text-destructive" : "text-muted-foreground"
        )}
      >
        {message}
      </span>
      {action}
    </div>
  );
}

/** One follow-up of a revealed value: an icon button named by its tooltip. */
function ChannelAction({
  label,
  isLast = false,
  asChild = false,
  onClick,
  children,
}: {
  label: string;
  isLast?: boolean;
  asChild?: boolean;
  onClick?: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          className={isLast ? "rounded-l-none" : "rounded-none"}
          aria-label={label}
          onClick={onClick}
          asChild={asChild}
        >
          {children}
        </Button>
      </TooltipTrigger>
      <TooltipContent>{label}</TooltipContent>
    </Tooltip>
  );
}

/**
 * A revealed email or phone, as on the company's suggested contact: the value
 * copies on click, and its follow-ups (write, call, draft a hook, report) sit
 * beside it as buttons — the card is wide enough for them, and a menu hid them.
 */
function RevealedChannel({
  channel,
  value,
  employee,
  onSendMessage,
  onReported,
}: {
  channel: ContactChannel;
  value: string;
  employee: EmployeeDTO;
  onSendMessage?: SendMessage;
  onReported: (motive: string) => void;
}) {
  const { t } = useTranslation();
  const marquee = useMarqueeCopy();
  const [showSignalDialog, setShowSignalDialog] = useState(false);
  const isEmail = channel === "email";

  const promptValues = {
    firstName: employee.firstName ?? "",
    lastName: employee.lastName ?? "",
    jobTitle: employee.jobTitle ?? "",
  };

  return (
    <div className="flex -space-x-px [&>button:first-child]:flex-1 [&>button:first-child]:justify-start">
      <CopyableContactButton
        value={value}
        copiedLabel={t("chat.message.contactReveal.copied")}
        marquee={marquee}
      />
      <ChannelAction
        label={
          isEmail
            ? t("components.dataTableCompanyEmployees.email.sendMail")
            : t("components.dataTableCompanyEmployees.phone.callPhone")
        }
        asChild
      >
        <a href={`${isEmail ? "mailto" : "tel"}:${value}`}>
          {isEmail ? <SendIcon /> : <PhoneCallIcon />}
        </a>
      </ChannelAction>
      {onSendMessage && (
        // Asked in this conversation, which already knows the contact.
        <ChannelAction
          label={
            isEmail
              ? t("components.dataTableCompanyEmployees.email.generateHook")
              : t("components.dataTableCompanyEmployees.phone.generateHook")
          }
          onClick={() =>
            onSendMessage(
              isEmail
                ? t(
                    "components.dataTableCompanyEmployees.email.promptMessage",
                    { ...promptValues, email: value }
                  )
                : t(
                    "components.dataTableCompanyEmployees.phone.promptMessage",
                    { ...promptValues, phone: value }
                  )
            )
          }
        >
          <BotIcon />
        </ChannelAction>
      )}
      <ChannelAction
        label={
          isEmail
            ? t("components.dataTableCompanyEmployees.email.signalEmailButton")
            : t("components.dataTableCompanyEmployees.phone.signalPhoneButton")
        }
        onClick={() => setShowSignalDialog(true)}
        isLast
      >
        <CircleAlertIcon />
      </ChannelAction>

      {isEmail ? (
        <SignalEmailDialog
          open={showSignalDialog}
          onOpenChange={setShowSignalDialog}
          employee={employee}
          onReported={onReported}
        />
      ) : (
        <SignalPhoneDialog
          open={showSignalDialog}
          onOpenChange={setShowSignalDialog}
          employee={employee}
          onReported={onReported}
        />
      )}
    </div>
  );
}

/**
 * Outcomes of a reveal, kept distinct on purpose.
 *
 * `notFound` and `stillSearching` used to render the same "not available": the
 * user was told the contact has no number when the provider was in fact still
 * looking (FullEnrich regularly runs past the polling window). They call for
 * opposite reactions — give up, versus come back in a minute — so they must not
 * look alike.
 */
type ChannelState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "value"; value: string }
  /** The provider completed its search and holds no number for this contact. */
  | { kind: "notFound" }
  /** We stopped polling, but the provider job is still running. */
  | { kind: "stillSearching" }
  /** The contact opted out — a retry can never succeed. */
  | { kind: "blacklisted" }
  /** The user reported the value as wrong: it is not offered any more. */
  | { kind: "reported" }
  /** The reveal could not be attempted (network, throttle, policy) — retryable. */
  | { kind: "error"; message: string };

/**
 * Classify a value-less reveal response.
 *
 * `pending` still true means the provider job outlived our polling window — the
 * number may well arrive. Only a settled response without a value is a real
 * "no number for this contact".
 */
const outcomeOf = (data: RevealApiResponse): ChannelState => {
  if (data.blacklisted) return { kind: "blacklisted" };
  if (data.pending || data.status === "PENDING")
    return { kind: "stillSearching" };
  return { kind: "notFound" };
};

/** Extract the API's machine-readable reveal error code, when present. */
const revealErrorCodeOf = (error: unknown): string | null => {
  const data = (
    error as { response?: { data?: { code?: unknown } } } | undefined
  )?.response?.data;
  return typeof data?.code === "string" ? data.code : null;
};

/**
 * Interactive per-channel row: reveals the email/phone in place on click by
 * calling the reveal endpoint directly (no round-trip through the assistant),
 * mirroring how the main search reveals a contact. Polls an async phone reveal
 * to completion, and stops polling as soon as the card unmounts so it cannot
 * keep hitting a paid endpoint from a thread the user has left.
 */
function ChannelReveal({
  ch,
  employee,
  onRevealed,
  onSendMessage,
}: {
  ch: PreviewChannel;
  employee: EmployeeDTO;
  /** Called once a reveal or a report lands, so the card refreshes its state. */
  onRevealed: () => void;
  onSendMessage?: SendMessage;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const { warnIfRateLimited } = useRateLimitWarning();
  const employeeId = employee.id;
  const isEmail = ch.channel === "email";
  const Icon = isEmail ? MailIcon : PhoneIcon;

  // "Nothing found" must survive a reload: the server already knows this team
  // searched and came back empty, so re-offering a paid reveal would charge for
  // a lookup whose answer we hold. It reopens on its own once the retry window
  // elapses, because the server stops reporting `searchedNotFound`.
  const [state, setState] = useState<ChannelState>(() => {
    if (ch.reported) return { kind: "reported" };
    if (ch.alreadyRevealed && ch.value) {
      return { kind: "value", value: ch.value };
    }
    return ch.searchedNotFound ? { kind: "notFound" } : { kind: "idle" };
  });

  // A phone reveal is asynchronous, so it is polled. Track liveness and the
  // pending timer so unmounting (switching conversation, closing the thread)
  // stops the chain instead of letting it keep POSTing and setting state on a
  // component that no longer exists.
  const isMounted = useRef(true);
  const pollTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const phoneConsumeCredits = useRef<boolean | undefined>(undefined);
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (pollTimer.current) clearTimeout(pollTimer.current);
    };
  }, []);

  const failWith = useCallback(
    (
      error: unknown,
      // Where a throttled call leaves the card: back to the button for a
      // reveal that never started, still waiting on a job already running —
      // its paid button again could start a second enrichment.
      whenRateLimited: ChannelState = { kind: "idle" }
    ) => {
      if (!isMounted.current) return;
      // Too many reveals in a row: the shared warning says when to try again.
      if (warnIfRateLimited(error)) {
        setState(whenRateLimited);
        return;
      }
      const code = revealErrorCodeOf(error);
      setState({
        kind: "error",
        message:
          code === "INSUFFICIENT_CREDITS"
            ? t("chat.message.contactReveal.notEnoughCredits")
            : code === "REVEAL_FORBIDDEN" || code === "EMAIL_LIMIT_REACHED"
              ? t("chat.message.contactReveal.revealForbidden")
              : t("chat.message.contactReveal.revealFailed"),
      });
    },
    [t, warnIfRateLimited]
  );

  const pollPhone = useCallback(
    async (attempt: number, consumeCredits?: boolean) => {
      if (!isMounted.current) return;
      try {
        const { data } = await api.post<RevealApiResponse>(
          "/contact-reveal/reveal",
          {
            employeeId,
            channel: "phone",
            resumeOnly: true,
            // The provider is asynchronous and the debit lands on whichever call
            // sees the delivery — so an admin's no-charge choice has to be
            // repeated on every poll, not just on the first request.
            ...(consumeCredits === false && { consumeCredits: false }),
          }
        );
        if (!isMounted.current) return;
        if (data.value) {
          setState({ kind: "value", value: data.value });
          onRevealed();
          return;
        }
        if (data.pending && attempt < MAX_POLL_ATTEMPTS) {
          pollTimer.current = setTimeout(
            () => void pollPhone(attempt + 1, consumeCredits),
            POLL_INTERVAL_MS
          );
          return;
        }
        setState(outcomeOf(data));
      } catch (error) {
        if (
          isMounted.current &&
          getApiErrorStatus(error) === 429 &&
          attempt < MAX_POLL_ATTEMPTS
        ) {
          warnIfRateLimited(error);
          const retryAfterSeconds = getApiRateLimit(error)?.retryAfterSeconds;
          const retryDelay =
            retryAfterSeconds !== undefined &&
            Number.isFinite(retryAfterSeconds) &&
            retryAfterSeconds > 0
              // Browser timers overflow past this signed 32-bit maximum.
              ? Math.min(retryAfterSeconds * 1000, 2_147_483_647)
              : POLL_INTERVAL_MS;
          pollTimer.current = setTimeout(
            () => void pollPhone(attempt + 1, consumeCredits),
            Math.max(POLL_INTERVAL_MS, retryDelay)
          );
          return;
        }
        failWith(error, { kind: "stillSearching" });
      }
    },
    [api, employeeId, failWith, onRevealed, warnIfRateLimited]
  );

  /**
   * Follow a job that outlived the polling window. Phone polls explicitly
   * resume only: a completed job must never start a second paid lookup.
   */
  const checkAgain = useCallback(async () => {
    setState({ kind: "loading" });
    if (!isEmail) {
      await pollPhone(1, phoneConsumeCredits.current);
      return;
    }
    try {
      const { data } = await api.post<ContactRevealStateResponse>(
        "/contact-reveal/state",
        { employeeId, channels: [ch.channel] }
      );
      if (!isMounted.current) return;
      const fresh = data.channels.find((c) => c.channel === ch.channel);
      if (fresh?.value) {
        setState({ kind: "value", value: fresh.value });
        onRevealed();
        return;
      }
      setState(
        fresh?.searchedNotFound ? { kind: "notFound" } : { kind: "stillSearching" }
      );
    } catch (error) {
      failWith(error, { kind: "stillSearching" });
    }
  }, [api, employeeId, ch.channel, failWith, onRevealed, isEmail, pollPhone]);

  // A paid reveal must be confirmed first, exactly like the employee table: the
  // click alone is not enough warning that credits are about to be spent. A free
  // channel (email, or a value the team already owns) goes straight through.
  const [showConfirm, setShowConfirm] = useState(false);

  const reveal = useCallback(
    async ({ consumeCredits }: { consumeCredits?: boolean } = {}) => {
      phoneConsumeCredits.current = consumeCredits;
      setState({ kind: "loading" });
      try {
        // Keep a minimum spinner time so a value already in DB doesn't flash.
        const { data } = await executeWithMinimumTime(
          () =>
            api.post<RevealApiResponse>("/contact-reveal/reveal", {
              employeeId,
              channel: ch.channel,
              ...(consumeCredits === false && { consumeCredits: false }),
            }),
          MIN_REVEAL_TIME_MS
        );
        if (!isMounted.current) return;
        if (data.value) {
          setState({ kind: "value", value: data.value });
          onRevealed();
          return;
        }
        if (data.pending) {
          void pollPhone(1, consumeCredits);
          return;
        }
        setState(outcomeOf(data));
      } catch (error) {
        failWith(error);
      }
    },
    [api, employeeId, ch.channel, pollPhone, failWith, onRevealed]
  );

  /** Warn before spending; reveal straight away when the channel is free. */
  const requestReveal = useCallback(() => {
    if (ch.cost > 0) {
      setShowConfirm(true);
      return;
    }
    void reveal();
  }, [ch.cost, reveal]);

  /**
   * A report settles the channel on the spot: a ban closes every channel of
   * the contact, so the card re-reads its state for the sibling row too.
   */
  const handleReported = (motive: string) => {
    setState(
      motive === "SIGNAL_BAN" ? { kind: "blacklisted" } : { kind: "reported" }
    );
    onRevealed();
  };

  const iconClassName = "size-4 shrink-0 text-muted-foreground";

  // What the server last said wins over what this row last showed: an opt-out
  // or a report may come from the other row or from elsewhere, and a value may
  // arrive through the CRM export, which reveals the email on its way.
  const shown: ChannelState = ch.blacklisted
    ? { kind: "blacklisted" }
    : ch.reported
      ? { kind: "reported" }
      : ch.alreadyRevealed &&
          ch.value &&
          (state.kind === "idle" || state.kind === "notFound")
        ? { kind: "value", value: ch.value }
        : state;

  const content =
    shown.kind === "value" ? (
      <RevealedChannel
        channel={ch.channel}
        value={shown.value}
        employee={employee}
        onSendMessage={onSendMessage}
        onReported={handleReported}
      />
    ) : shown.kind === "loading" ? (
      <Button type="button" className="w-full" disabled>
        <Spinner className="size-4" />
        {isEmail
          ? t("chat.message.contactReveal.revealing")
          : t("chat.message.contactReveal.phonePending")}
      </Button>
    ) : shown.kind === "notFound" ? (
      // Definitive for now: the provider searched and holds nothing. No retry
      // offered — it would spend another paid round-trip for the same answer.
      <ChannelStatusRow
        icon={<Icon className={iconClassName} />}
        message={
          isEmail
            ? t("chat.message.contactReveal.notFoundEmail")
            : t("chat.message.contactReveal.notFoundPhone")
        }
      />
    ) : shown.kind === "stillSearching" ? (
      // The job outlived our polling window — the number may still arrive, so
      // offer to check again rather than declaring it missing.
      <ChannelStatusRow
        icon={<Icon className={iconClassName} />}
        message={t("chat.message.contactReveal.stillSearching")}
        action={
          <Button type="button" size="xs" variant="outline" onClick={checkAgain}>
            {t("chat.message.contactReveal.checkAgain")}
          </Button>
        }
      />
    ) : shown.kind === "blacklisted" ? (
      <ChannelStatusRow
        icon={<BanIcon className="size-4 shrink-0 text-destructive" />}
        message={t("chat.message.contactReveal.blacklisted")}
        tone="destructive"
      />
    ) : shown.kind === "reported" ? (
      <ChannelStatusRow
        icon={<CircleAlertIcon className={iconClassName} />}
        message={t("chat.message.contactReveal.reported")}
      />
    ) : shown.kind === "error" ? (
      // Retryable: a throttle hit or a network blip must not look like
      // "this contact has no phone number".
      <ChannelStatusRow
        icon={<Icon className={iconClassName} />}
        message={shown.message}
        tone="destructive"
        action={
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={requestReveal}
          >
            {t("chat.message.contactReveal.retry")}
          </Button>
        }
      />
    ) : (
      // The price is not on the button: the credits gate states it before
      // anything is spent. The button stays available even when the balance
      // is short — the gate then offers to top up (or to ask an admin), which
      // an inert "not enough credits" label could not.
      <Button type="button" className="w-full" onClick={requestReveal}>
        <Icon className="size-4" />
        {isEmail
          ? t("chat.message.contactReveal.getEmail")
          : t("chat.message.contactReveal.getPhone")}
      </Button>
    );

  return (
    <>
      {content}

      {/* Same cost confirmation and admin no-charge choice as the employee table. */}
      <PhoneRevealCreditsGate
        open={showConfirm}
        onOpenChange={setShowConfirm}
        onConfirm={() => void reveal()}
        onConfirmWithoutCost={() => void reveal({ consumeCredits: false })}
      />
    </>
  );
}

/**
 * Per-channel rows backed by live state.
 *
 * The cost, balance, blacklist flag and already-owned value are always fetched
 * from the server on mount rather than read from the message: a persisted card
 * has them stripped, and even a fresh one goes stale as soon as credits are
 * spent elsewhere or the contact opts out. Without this, a reopened thread would
 * quote an outdated price and could offer a contact that must no longer be
 * revealed.
 */
function ChannelList({
  employeeId,
  contact,
  channels,
  onSendMessage,
}: {
  employeeId: string;
  contact: PreviewContact;
  channels: PersistedChannel[];
  onSendMessage?: SendMessage;
}) {
  const { t } = useTranslation();
  const api = useApi();
  const connectedCrmIntegrations = useConnectedCrmIntegrations();
  const { teamSelected } = useTeams();
  const isEmailChannelOpen =
    teamSelected?.featureContactRevealEmailEnabled ?? true;
  const [live, setLive] = useState<ContactRevealStateResponse | null>(null);
  const [failed, setFailed] = useState(false);
  // What the CRM export reported, kept so a re-render does not show the
  // contact as never sent.
  const [integrations, setIntegrations] = useState<
    EmployeeDTO["integrations"]
  >({});
  const isMounted = useRef(true);

  const channelKeys = channels
    .map((c) => c.channel)
    .filter((c) => c === "email" || c === "phone")
    .join(",");

  const loadState = useCallback(async () => {
    if (!channelKeys) return;
    try {
      const { data } = await api.post<ContactRevealStateResponse>(
        "/contact-reveal/state",
        { employeeId, channels: channelKeys.split(",") }
      );
      if (!isMounted.current) return;
      // Clear a previous failure: a success must win, otherwise one transient
      // error would keep the card in its error state for the rest of the
      // session even though fresh data arrived.
      setFailed(false);
      setLive(data);
    } catch {
      if (isMounted.current) setFailed(true);
    }
  }, [api, employeeId, channelKeys]);

  useEffect(() => {
    isMounted.current = true;
    void loadState();
    return () => {
      isMounted.current = false;
    };
  }, [loadState]);

  /**
   * Re-read after a reveal lands so the remaining cost reflects what was just
   * spent — otherwise the sibling channel keeps a price computed before the
   * debit, and the CRM export would not carry the new value.
   */
  const handleRevealed = useCallback(() => {
    void loadState();
  }, [loadState]);

  const revealedValueOf = (channel: ContactChannel): string | null => {
    const match = live?.channels.find((c) => c.channel === channel);
    return match?.alreadyRevealed && !match.blacklisted && !match.reported
      ? match.value
      : null;
  };
  const email = revealedValueOf("email");
  const phone = revealedValueOf("phone");
  const companyId = live?.company?.id;
  const companyName = live?.company?.name ?? null;

  const employee = useMemo(
    () =>
      toEmployeeDTO(
        employeeId,
        contact,
        { email, phone },
        integrations,
        isEmailChannelOpen,
        companyName
      ),
    [
      employeeId,
      contact,
      email,
      phone,
      integrations,
      isEmailChannelOpen,
      companyName,
    ]
  );

  /** The export may reveal the email on its way to the CRM: re-read it then. */
  const handleSynced = (update: Partial<EmployeeDTO> & { id: string }) => {
    if (update.integrations) setIntegrations(update.integrations);
    if (update.email) void loadState();
  };

  // Nothing to price (malformed/legacy persisted output) — render the identity
  // card alone rather than a stuck spinner.
  if (!channelKeys) {
    return null;
  }

  // Recoverable in place: reloading the page is not a fix when the failure is a
  // transient one (throttle, network blip), and it loses the user's scroll.
  if (failed && !live) {
    return (
      <ChannelStatusRow
        icon={<CircleAlertIcon className="size-4 shrink-0 text-muted-foreground" />}
        message={t("chat.message.contactReveal.stateUnavailable")}
        action={
          <Button
            type="button"
            size="xs"
            variant="outline"
            onClick={() => void loadState()}
          >
            {t("chat.message.contactReveal.retry")}
          </Button>
        }
      />
    );
  }

  if (!live) {
    return (
      <div
        className="flex flex-col gap-2"
        aria-busy
        aria-label={t("chat.message.contactReveal.loadingState")}
      >
        {channelKeys.split(",").map((channel) => (
          <Skeleton key={channel} className="w-full h-9" />
        ))}
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2">
      {live.channels.map((ch) => (
        <ChannelReveal
          key={ch.channel}
          ch={ch}
          employee={employee}
          onRevealed={handleRevealed}
          onSendMessage={onSendMessage}
        />
      ))}
      {connectedCrmIntegrations.length > 0 && (
        // Linked to the company of the base when the domain points to one, as
        // on the company page; otherwise a loose contact carrying the name.
        <EmployeeIntegrationsSync
          employee={employee}
          companyId={companyId}
          onSynced={handleSynced}
          showLabel
        />
      )}
    </div>
  );
}

/**
 * Resolves the homonym the user picked, in place.
 *
 * The pick used to be sent to the assistant as a chat message carrying a
 * `[@shaarp/contact-id=...]` marker. That cost a full turn and broke down as soon
 * as one message produced several candidate tables: the model received several
 * pending ambiguities and a single marker, could not tell which table the pick
 * answered, resolved one and re-emitted tables for the others — so every click
 * produced a fresh round of tables. Resolving against the API instead makes each
 * table independent, instant, and free of the assistant entirely.
 */
function useCandidateResolution(channels: ContactChannel[]) {
  const api = useApi();
  const [pending, setPending] = useState<string | null>(null);
  const [resolved, setResolved] = useState<ContactRevealSelection | null>(null);
  const [failed, setFailed] = useState(false);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const pick = useCallback(
    async (key: string, candidate: PreviewCandidate) => {
      setPending(key);
      setFailed(false);
      try {
        const { data } = await api.post<ContactRevealSelection>(
          "/contact-reveal/select",
          {
            ...(candidate.employeeId && { employeeId: candidate.employeeId }),
            ...(candidate.apolloId && { apolloId: candidate.apolloId }),
            channels,
          }
        );
        if (!isMounted.current) return;
        setResolved(data);
      } catch {
        if (isMounted.current) setFailed(true);
      } finally {
        if (isMounted.current) setPending(null);
      }
    },
    [api, channels]
  );

  return { pick, pending, resolved, failed };
}

/**
 * Renders the `previewContactReveal` tool part: the identified contact and,
 * per channel, its cost — with an unlock button that reveals the value in place
 * (nothing is spent without an explicit click).
 */
export function ContactRevealPreviewCard({
  part,
  live,
  onSendMessage,
}: {
  part: ToolUIPart;
  live: boolean;
  /**
   * Sends a follow-up in this conversation (draft a hook email, a call
   * script). Absent where the card is not part of a live thread.
   */
  onSendMessage?: SendMessage;
}) {
  const { t } = useTranslation();
  // Both channels: the picked contact is priced for whatever the user may want,
  // and the assistant's original `channel` input is not carried in the output.
  const selection = useCandidateResolution(SELECTABLE_CHANNELS);
  const isContactRevealOffered = useIsContactRevealOffered();

  // A card from an older conversation, on a team that has since lost its key
  // contacts or both its channels: its unlock button would only be refused
  // (#757, #840).
  if (!isContactRevealOffered) {
    return null;
  }

  // Terminal failure state: the tool never produced an output. Render the error
  // rather than a spinner, which would otherwise hang forever — including after
  // a reload, since the errored part is persisted. A part still pending outside
  // the live stream is dead too: nothing will ever complete it.
  if (
    part.state === "output-error" ||
    (!live && part.state !== "output-available")
  ) {
    return (
      <Card className={CARD_CLASS}>
        <p className="text-sm text-muted-foreground">
          {t("chat.message.contactReveal.identifyFailed")}
        </p>
      </Card>
    );
  }

  if (part.state !== "output-available") {
    return (
      <Card className={CARD_CLASS}>
        <div className="flex gap-2 items-center text-sm text-muted-foreground">
          <Spinner className="size-3.5" />
          <span>{t("chat.message.contactReveal.identifying")}</span>
        </div>
      </Card>
    );
  }

  // A stream aborted mid-tool can persist a part with no output — bail out
  // gracefully instead of crashing.
  const output = part.output as PreviewOutput | undefined;
  if (!output) {
    return null;
  }

  // The team may not use contact enrichment at all (blocked, or both channels
  // disabled) — say so instead of "no contact found", which would send the user
  // hunting for a better identifier that could never work.
  if (output.status === "forbidden") {
    return (
      <Card className={CARD_CLASS}>
        <p className="flex gap-2 items-center text-sm text-muted-foreground">
          <BanIcon className="size-3.5 text-destructive" />
          {t("chat.message.contactReveal.revealForbidden")}
        </p>
      </Card>
    );
  }

  // The contact was found, but the channel asked for (e.g. phone) is not part
  // of the team's plan — a neutral, fixed statement, not a card offering a
  // button doomed to fail, and not the model narrating a reason on its own.
  if (output.status === "channel_unavailable") {
    return (
      <Card className={CARD_CLASS}>
        <p className="flex gap-2 items-center text-sm text-muted-foreground">
          <BanIcon className="size-3.5 text-muted-foreground" />
          {t("chat.message.contactReveal.channelNotIncluded")}
        </p>
      </Card>
    );
  }

  // A backend failure is not the same statement as "this person does not exist":
  // telling the user "no contact found" sends them hunting for a better
  // identifier when the lookup simply broke.
  if (output.status === "error") {
    return (
      <Card className={CARD_CLASS}>
        <p className="text-sm text-muted-foreground">
          {t("chat.message.contactReveal.identifyFailed")}
        </p>
      </Card>
    );
  }

  if (output.status === "not_found") {
    return (
      <Card className={CARD_CLASS}>
        <p className="text-sm text-muted-foreground">
          {t("chat.message.contactReveal.notFound")}
        </p>
      </Card>
    );
  }

  if (output.status === "ambiguous") {
    // A pick resolves in place: the table is replaced by the contact card, so
    // several tables in one message each settle on their own.
    if (selection.resolved) {
      return (
        <ResolvedContactCard
          employeeId={selection.resolved.employeeId}
          contact={selection.resolved.contact}
          channels={selection.resolved.channels}
          onSendMessage={onSendMessage}
        />
      );
    }

    return (
      <Card className={AMBIGUOUS_CARD_CLASS}>
        <p className="text-sm font-medium">
          {t("chat.message.contactReveal.pickContact")}
        </p>
        {selection.failed && (
          <p className="text-xs text-destructive">
            {t("chat.message.contactReveal.selectFailed")}
          </p>
        )}
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>
                  {t("chat.message.contactReveal.columnContact")}
                </TableHead>
                <TableHead>
                  {t("chat.message.contactReveal.columnRole")}
                </TableHead>
                <TableHead>
                  {t("chat.message.contactReveal.columnLocation")}
                </TableHead>
                <TableHead className="text-right" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {output.candidates.map((candidate, index) => {
                const role =
                  [candidate.jobTitle, candidate.company]
                    .filter(Boolean)
                    .join(" · ") || "—";
                const location = locationOf(candidate) || "—";
                const candidateKey =
                  candidate.employeeId ?? candidate.apolloId ?? String(index);
                return (
                  <TableRow key={candidateKey}>
                    <TableCell>
                      <div className="flex gap-2 items-center">
                        <Avatar className="size-8 shrink-0">
                          <AvatarImage
                            src={candidate.photoUrl || ""}
                            alt={candidate.firstName || ""}
                          />
                          <AvatarFallback>
                            {initialsOf(candidate)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium">
                          {fullName(candidate) || "—"}
                        </span>
                        {candidate.linkedInUrl && (
                          <a
                            href={candidate.linkedInUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="LinkedIn"
                            className="text-[#0A66C2] hover:opacity-80"
                          >
                            <RiLinkedinFill className="size-4" />
                          </a>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {role}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {location}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        type="button"
                        size="sm"
                        // Only the row being resolved is busy; the others stay
                        // clickable so a mis-click can be corrected right away.
                        disabled={selection.pending !== null}
                        onClick={() => void selection.pick(candidateKey, candidate)}
                      >
                        {selection.pending === candidateKey
                          ? t("chat.message.contactReveal.choosing")
                          : t("chat.message.contactReveal.choose")}
                      </Button>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </Card>
    );
  }

  if (output.status !== "ready") {
    return null;
  }

  return (
    <ResolvedContactCard
      employeeId={output.employeeId}
      contact={output.contact}
      channels={output.channels}
      onSendMessage={onSendMessage}
    />
  );
}

/**
 * The identified contact plus its per-channel unlock rows.
 *
 * Shared by the assistant's `ready` output, by an in-place candidate pick and
 * by a people table read on a phone, so a contact reached any way looks and
 * behaves identically.
 */
export function ResolvedContactCard({
  employeeId,
  contact,
  channels,
  onSendMessage,
}: {
  employeeId: string;
  contact: PreviewContact;
  channels: PersistedChannel[];
  onSendMessage?: SendMessage;
}) {
  const name = fullName(contact);
  // Shown so the user can catch a homonym resolved in the wrong place before
  // paying — the city/company hint they gave may not match the data.
  const details = [contact.jobTitle, contact.company, locationOf(contact)]
    .filter(Boolean)
    .join(" · ");

  return (
    <Card className={CARD_CLASS}>
      <div className="flex gap-3 items-center">
        <Avatar className="size-11 shrink-0">
          <AvatarImage
            src={contact.photoUrl || ""}
            alt={contact.firstName || ""}
          />
          <AvatarFallback className="text-sm font-semibold">
            {initialsOf(contact)}
          </AvatarFallback>
        </Avatar>
        <div className="flex flex-col flex-1 min-w-0">
          <span className="text-sm font-semibold truncate">
            {name || contact.company || "—"}
          </span>
          {details && (
            <span className="text-xs truncate text-muted-foreground">
              {details}
            </span>
          )}
        </div>
        {contact.linkedInUrl && (
          <Button
            size="icon"
            variant="ghost"
            className="shrink-0"
            asChild
          >
            <a
              href={contact.linkedInUrl}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="LinkedIn"
            >
              <RiLinkedinBoxFill className="size-5 text-[#0a66c2]" />
            </a>
          </Button>
        )}
      </div>

      <ChannelList
        employeeId={employeeId}
        contact={contact}
        channels={channels}
        onSendMessage={onSendMessage}
      />
    </Card>
  );
}
